import {
	Dialog,
	Form,
	FormItem,
	renderReactDialog,
	Toolbar
} from "client/Components";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	createValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind,
	IRootValidation,
	IValidationConfiguration
} from "client/Validation";
import {
	inline,
	isNotEmpty
} from "client/Validation/rules";
import { getExtension, getFileNameWithoutExtension } from "client_legacy/Documents/path";
import { isValid } from "client_legacy/Documents/WebDav/documentNamePolicy";
import { publish, WebDavEvent } from "client_legacy/Documents/WebDav/events";
import { rename, RenameModel } from "client/Api/Documents/WebDav/webDavActions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { TextInputWithExtension } from "../../Dialogs/TextInputWithExtension";
import { FileSystemItemModel } from "client/Api/Documents/WebDav/webDav";

type RenameDialogProps = {
	serverId: number;
	document: FileSystemItemModel;
	onClose: () => void;
};

const RenameDialog = (props: RenameDialogProps): ReactElement => {
	const {
		serverId,
		document,
		onClose
	} = props;

	const [isBusy, setIsBusy] = useState<boolean>(false);
	const[formData, setFormData] = useState<FormData>(
		(): FormData => convertToFormData(document)
	);

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();

	const formHandler: IFormHandler<FormData> = useFormHandler(
		formData,
		setFormData,
		validationConfiguration
	);

	const submitHandler = useCallback(
		async (): Promise<void> => {
			if (isBusy) {
				return;
			}

			setIsBusy(true);

			const validation: IRootValidation<FormData> = formHandler.validation;

			try {
				const validationResult: InputValidationResult = await validation.validate(globalCancellation);

				if (validationResult.kind !== InputValidationResultKind.Valid) {
					setIsBusy(false);
					return;
				}

				const model: RenameModel = {
					itemId: document.id,
					name: (formData.name + formData.extension).trim()
				};

				if (model.name === document.name) {
					onClose();
					return;
				}

				const item: FileSystemItemModel = await rename(
					serverId,
					model,
					globalCancellation
				);

				publish(
					WebDavEvent.Renamed,
					{
						serverId: serverId,
						oldItemId: document.id,
						item: item
					}
				);

				onClose();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				if (!validation.tryProcessServerError(e)) {
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}

				setIsBusy(false);
			}
		},
		[
			alertManager,
			document.id,
			document.name,
			formData.extension,
			formData.name,
			formHandler.validation,
			globalCancellation,
			isBusy,
			onClose,
			serverId
		]
	);

	return (
		<Dialog
			trackingId="webdav-rename-dialog"
			hasBusyIndicator={isBusy}
		>
			<Dialog.Header
				title={localize("_Rename")}
				onClose={onClose}
			/>

			<Dialog.Body>
				<Form onSubmit={submitHandler}>
					<FormItem>
						<TextInputWithExtension
							attachable={formHandler.for("name")}
							fileExtension={formData.extension}
							placeholder={localize("_EnterNewName")}
							isMobile={isMobile}
						/>
					</FormItem>
				</Form>
			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						trackingId="rename"
						text={localize("_Rename")}
						onClick={submitHandler}
					/>

					<Toolbar.Button
						kind="secondary"
						trackingId="cancel"
						text={localize("_Cancel")}
						onClick={onClose}
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
RenameDialog.displayName = "RenameDialog";

type FormData = {
	extension: string;
	name: string;
};

function convertToFormData(item: FileSystemItemModel): FormData {
	let name: string = item.name;
	let extension = "";

	if (!item.isFolder) {
		extension = getExtension(item.name);
		if (extension !== "") {
			name = getFileNameWithoutExtension(name);
		}
	}

	return {
		name: name,
		extension
	};
}

const validationConfiguration: IValidationConfiguration<FormData> = createValidationConfiguration<FormData>();

validationConfiguration.forScalar("name")
	.add(
		isNotEmpty(),
		inline(
			(value): InputValidationResult => {
				if (isValid(value)) {
					return {
						kind: InputValidationResultKind.Valid
					};
				}

				return {
					kind: InputValidationResultKind.Invalid,
					errorMessage: localize("_ContainsIllegalCharactersForWebDav")
				};
			}
		)
	);

export function openRenameDialog(props: Omit<RenameDialogProps, "onClose">): void {
	renderReactDialog(
		(onClose) => (
			<RenameDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}
